import { OpenPanel } from "@openpanel/web";

/**
 * Browser-side OpenPanel bootstrap, the counterpart of MispanelConfig's mixPanelConfig().
 * Runs alongside Mixpanel rather than replacing it -- both are initialised from Layout.
 *
 * No client secret is used here on purpose: a secret in the bundle is a published secret.
 * The browser client is a write-only client id; the secret stays server-side in
 * Analytics::OpenpanelService.
 */

/**
 * Identity rendered into the page by layouts/_openpanel.html.erb. `profileId` is the
 * user id the Rails side uses as OpenPanel's profileId, so browser and server events
 * land on the same profile and a funnel can span the two.
 */
export interface OpenpanelIdentity {
  profileId: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  groupId?: string;
  properties?: Record<string, string | number | boolean | null>;
}

declare global {
  interface Window {
    openpanel?: OpenPanel;
    qoyodOpenpanelIdentity?: OpenpanelIdentity;
  }
}

/**
 * Self-hosted: there is no default host. An unset api url disables tracking instead of
 * falling back to OpenPanel Cloud, matching OpenpanelConfig on the Rails side.
 */
const clientId = process.env.FRONT_END_OPENPANEL_CLIENT_ID;
const apiUrl = process.env.FRONT_END_OPENPANEL_API_URL;

export const openpanelEnabled = (): boolean => Boolean(clientId && apiUrl);

export const openpanelConfig = (): OpenPanel | undefined => {
  if (typeof window === "undefined") return undefined;
  if (!openpanelEnabled()) return undefined;
  // Layout and App both call this, and a remount must not produce a second client.
  if (window.openpanel) return window.openpanel;

  const op = new OpenPanel({
    clientId: clientId!,
    apiUrl,
    // Screen views give the denominator for every adoption question ("of the users who
    // opened the invoices list, how many used a bulk action").
    trackScreenViews: true,
    trackOutgoingLinks: false,
    // Lets a server-rendered ERB button report itself with data-track / data-* alone.
    trackAttributes: true,
  });

  const identity = window.qoyodOpenpanelIdentity;
  if (identity?.profileId) {
    void op.identify({
      profileId: identity.profileId,
      firstName: identity.firstName,
      lastName: identity.lastName,
      email: identity.email,
      properties: identity.properties,
    });

    // OpenPanel never auto-populates groups onto events, so the tenant is set as a
    // global property too -- otherwise every browser event would be un-sliceable by org.
    if (identity.groupId) {
      op.setGlobalProperties({ tenant_group: identity.groupId });
    }
  }

  if (identity?.properties) {
    op.setGlobalProperties(identity.properties);
  }

  window.openpanel = op;
  return op;
};

/**
 * Safe entry point for the parts of the app that aren't React -- the ERB bulk-actions
 * drawer reaches OpenPanel through window.openpanel, which is absent until Layout mounts
 * and stays absent when the integration isn't configured.
 */
export const trackOpenpanel = (
  event: string,
  properties?: Record<string, string | number | boolean | undefined>,
): void => {
  void window.openpanel?.track(event, properties);
};
